/**
 * Pinned PCOs — the rail's "keep this one on top" list.
 *
 * Distinct from `railPref.ts` (which pins the rail itself open): this pins
 * individual PCOs into a "Pinned" group that renders above "Recent PCOs".
 * Recent is algorithmic and changes under the PM week to week; a pin is the
 * PM saying "I'm living in this one right now", and it stays until unpinned.
 *
 * Per-project on purpose: the rail's PCO list is project-scoped, and a pin
 * carried across a project switch would reference a PCO that isn't in the
 * list. Stored per browser (localStorage, not the DB) — it's a personal
 * workspace preference, same tier as the rail-open choice.
 *
 * Stale ids are harmless: a pinned PCO that goes paid/void (or is deleted)
 * simply stops matching anything and renders nowhere. The list is small and
 * user-curated, so no pruning.
 *
 * Storage guard copied from `railPref.ts`: the web Vitest project runs in
 * `node`, and Safari private mode throws on access.
 */

const KEY_PREFIX = 'coffman-tm:rail-pco-pins:';

export function railPcoPinsKey(projectId: string): string {
  return `${KEY_PREFIX}${projectId}`;
}

function localStore(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** Pinned PCO ids for a project. Empty on a fresh browser, bad JSON, or no Storage. */
export function getRailPcoPins(projectId: string | null): string[] {
  if (!projectId) return [];
  const store = localStore();
  if (!store) return [];
  try {
    const parsed: unknown = JSON.parse(store.getItem(railPcoPinsKey(projectId)) ?? '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === 'string');
  } catch {
    return [];
  }
}

/**
 * Toggle one PCO's pin and persist. Returns the new list either way — a
 * failed write (quota, private mode) still updates the in-memory rail for
 * this page load; it's a preference, not data.
 */
export function toggleRailPcoPin(projectId: string | null, pcoId: string): string[] {
  const current = getRailPcoPins(projectId);
  const next = current.includes(pcoId)
    ? current.filter((id) => id !== pcoId)
    : [...current, pcoId];
  if (projectId) {
    try {
      localStore()?.setItem(railPcoPinsKey(projectId), JSON.stringify(next));
    } catch {
      // Preference persistence is best-effort.
    }
  }
  return next;
}
